import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { AskBennyButton } from '@/components/assistant/AskBennyButton'
import { useAuth } from '@/components/auth/AuthProvider'
import {
  PortalAskBennyButton,
  usePortalAskBennyAvailable,
} from '@/components/client-portal/benny/PortalAskBennyButton'
import { ClientPortalAccessSwitcher } from '@/components/client-portal/ClientPortalAccessSwitcher'
import { ClientPortalNavModeMenu } from '@/components/client-portal/ClientPortalNavModeMenu'
import { ClientPortalNotificationCenter } from '@/components/client-portal/ClientPortalNotificationCenter'
import { RecordButton } from '@/components/desktop/RecordButton'
import { ModeToggle } from '@/components/mode-toggle'
import { useTheme } from '@/components/theme-provider'
import { TimeEntryWidget } from '@/components/time-entry'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import { NAVIGATION_STYLE } from '@/config/rail-areas'
import { useTauriTitleBar } from '@/contexts/TauriTitleBarContext'
import { useAccountType } from '@/hooks/useAccountType'
import { useAutoUpdater } from '@/hooks/useAutoUpdater'
import { reloadPageWithRecordingGuard } from '@/hooks/useRecordingUnloadWarning'
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode'
import { useTauriLiquidGlass } from '@/hooks/useTauriLiquidGlass'
import { cn } from '@/lib/utils'
import { getShareableWebUrl } from '@/lib/utils/app-links'
import { useClientPortalStore } from '@/store/client-portal.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import { useUpdateStore } from '@/store/update.store'
import {
  ArrowClockwiseIcon,
  ArrowLeftIcon,
  ArrowsClockwise,
  ArrowSquareOutIcon,
  CaretDownIcon,
  Download,
  ShareNetworkIcon,
  SidebarSimpleIcon,
} from '@phosphor-icons/react'
import { m } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { FloatingNavModeMenu } from './FloatingNavModeMenu'
import { NotificationButton } from './NotificationButton'
import { OmniSearch } from './OmniSearch'
import { OrganizationSwitcher } from './OrganizationSwitcher'
import { UserMenu } from './UserMenu'

interface TauriTitleBarProps {
  onOpenChange?: (open: boolean) => void
}

function useTitleBarTextMode() {
  const sidebarTheme = useUIPreferencesStore(s => s.sidebarTheme)
  const { theme } = useTheme()
  const isDarkMode = useResolvedDarkMode(theme)
  // Personal spaces override the sidebar theme with hearth chrome (cream in
  // light, warm black in dark — see colors.css), so text mode must follow the
  // resolved light/dark mode, not the work sidebar theme. The DOM attribute is
  // stamped synchronously at boot (personal-theme.ts), so cold-boot popup
  // windows read correctly before the org store hydrates; useAccountType keeps
  // it reactive across org switches.
  const { isPersonal } = useAccountType()
  const isPersonalTheme =
    isPersonal ||
    (typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-account-type') === 'personal')

  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]
  const useDarkText = isPersonalTheme
    ? !isDarkMode
    : isDarkMode
      ? false
      : themeConfig.lightTextColor === 'dark'

  return { useDarkText, isPersonalTheme }
}

// Island entrance on the app's iOS curve (index.css MOTION TOKENS): each
// group settles in from just above, left to right.
const ISLAND_ENTER = { duration: 0.32, ease: [0.32, 0.72, 0, 1] as const }
const ISLAND_STAGGER = 0.05

/**
 * One group of title-bar controls. In the floating navigation style it is a
 * glass pill that flows in on mount; in the docked styles it renders as
 * `display: contents` so the bar's original flex gaps apply unchanged.
 */
function TitleBarIsland({
  floating,
  index,
  className,
  dockedClassName = 'contents',
  layout,
  children,
}: {
  floating: boolean
  /** Left-to-right position, for the entrance stagger. */
  index: number
  className?: string
  dockedClassName?: string
  /** Animate size changes (e.g. the timer pill appearing) instead of jumping. */
  layout?: boolean
  children: ReactNode
}) {
  if (!floating) {
    return <div className={dockedClassName}>{children}</div>
  }
  return (
    <m.div
      layout={layout}
      className={cn(
        'floating-nav-island floating-titlebar-island flex h-10 items-center gap-1 px-1.5',
        className,
      )}
      data-tauri-drag-region
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...ISLAND_ENTER, delay: ISLAND_STAGGER * index }}
    >
      {children}
    </m.div>
  )
}

function TauriUpdateButton() {
  const { updateAvailable, dismissed, installing } = useUpdateStore()
  const { installUpdate } = useAutoUpdater({ enableListeners: false })

  if (!updateAvailable || !dismissed) {
    return null
  }

  const buttonLabel = installing ? 'Updating...' : 'Install'

  const buttonIcon = installing ? (
    <ArrowsClockwise className="h-3 w-3 animate-spin !text-white" />
  ) : (
    <Download className="h-3 w-3 !text-white" weight="bold" />
  )

  return (
    <Button
      variant="default"
      size="sm"
      data-update-pill
      onClick={() => void installUpdate()}
      disabled={installing}
      className="update-pill-force h-7 gap-1 rounded-full !border-blue-500/70 !bg-blue-600 px-2.5 text-xs font-medium !text-white hover:!bg-blue-500 hover:!text-white [&_svg]:!text-white"
      title="Install available update"
    >
      {buttonIcon}
      <span className="!text-white">{buttonLabel}</span>
    </Button>
  )
}

/**
 * TauriTitleBar - A Stack-like title bar for the Tauri desktop app
 *
 * Features:
 * - Fixed position at the top of the window
 * - Draggable title bar using Tauri's startDragging() API
 * - Sidebar toggle on the left
 * - Centered OmniSearch that's also draggable when not focused
 * - Timer widget, organization switcher and user menu on the right
 * - Clean, minimal design inspired by Stack and Linear
 *
 * Traffic light position is set via trafficLightPosition in tauri.conf.json
 */
export function TauriTitleBar({ onOpenChange }: TauriTitleBarProps) {
  const { toggleSidebar, state } = useSidebar()
  const setSidebarOpen = useUIPreferencesStore(s => s.setSidebarOpen)
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  // Personal spaces are detected the same way the text mode is (store, with
  // the boot-stamped attribute as the cold-start fallback).
  const { useDarkText, isPersonalTheme } = useTitleBarTextMode()
  // Floating navigation style (config/rail-areas.ts): the bar stops being a
  // solid strip and becomes part of the tinted ground the sidebar islands
  // float on, with its controls gathered into matching glass islands (admin
  // mode included — its sidebar is an island too). Personal spaces keep the
  // docked sidebar, so they keep the docked bar (AppSidebar makes the same
  // call).
  const isFloatingChrome = NAVIGATION_STYLE === 'floating' && !isPersonalTheme
  const navigate = useNavigate()
  const { setTitleBarActive } = useTauriTitleBar()
  // The search collapses to a round button on the right until it's in use;
  // its island sizes around whichever state it reports.
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  useTauriLiquidGlass()
  // Mark title bar as active when mounted, inactive when unmounted
  useEffect(() => {
    setTitleBarActive(true)
    return () => setTitleBarActive(false)
  }, [setTitleBarActive])

  const handleToggleSidebar = () => {
    toggleSidebar()
    setSidebarOpen(state === 'collapsed')
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    reloadPageWithRecordingGuard()
  }

  const handlePopOut = async () => {
    const { openFocusWindow } = await import('@/lib/utils/tauri')
    const path = window.location.pathname
    const title = document.title.replace(' - Scrambl', '').trim() || 'Scrambl'
    await openFocusWindow(path, title)
  }

  const handleShare = async () => {
    const url = getShareableWebUrl()
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && 'tauri-glass-titlebar',
        isFloatingChrome && 'floating-titlebar',
      )}
      data-tauri-drag-region
      // Style hooks shared with the app shell frame (theme.css): same ground
      // tint and island glass, lighter when native glass is showing through.
      data-nav-style={isFloatingChrome ? 'floating' : 'docked'}
      data-glass={tauriGlassEnabled ? 'true' : 'false'}
    >
      {/* Title bar with drag region and controls - 52px height for centered traffic lights */}
      {/* CSS in index.css handles webkit-app-region for drag/no-drag based on data-tauri-drag-region */}
      <div
        // titlebar-row is a size container: the window actions below show
        // inline or fold behind the chevron based on the bar's width.
        className={cn('titlebar-row flex h-[52px] w-full items-center')}
        data-tauri-drag-region
      >
        {/* Left section - traffic light spacer + navigation + sidebar toggle + timer */}
        <div
          className={cn(
            'flex h-full flex-shrink-0 items-center pl-[100px] pr-3',
            isFloatingChrome ? 'gap-2' : 'gap-1',
          )}
          data-tauri-drag-region
        >
          {/* Window navigation island */}
          <TitleBarIsland floating={isFloatingChrome} index={0}>
            {/* Back navigation button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className={cn(
                'h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Go back"
            >
              <ArrowLeftIcon className="h-[16px] w-[16px]" weight="bold" />
              <span className="sr-only">Go back</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className={cn(
                'h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Refresh"
            >
              <ArrowClockwiseIcon className="h-[16px] w-[16px]" weight="bold" />
              <span className="sr-only">Refresh</span>
            </Button>
            {/* Secondary window actions: inline when the bar is wide enough
                (theme.css .titlebar-window-action-inline), otherwise folded
                behind the chevron so narrow windows stay tight. */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void handlePopOut()}
              className={cn(
                'titlebar-window-action-inline h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Open in new window"
            >
              <ArrowSquareOutIcon className="h-[16px] w-[16px]" weight="bold" />
              <span className="sr-only">Open in new window</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void handleShare()}
              className={cn(
                'titlebar-window-action-inline h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Copy web link"
            >
              <ShareNetworkIcon className="h-[16px] w-[16px]" weight="bold" />
              <span className="sr-only">Copy web link</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'titlebar-window-actions-menu h-8 w-6',
                    useDarkText
                      ? 'text-black/50 hover:bg-black/10 hover:text-black data-[state=open]:bg-black/10 data-[state=open]:text-black'
                      : 'text-white/50 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white',
                  )}
                  title="More window actions"
                >
                  <CaretDownIcon className="h-[13px] w-[13px]" weight="bold" />
                  <span className="sr-only">More window actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[10rem]">
                <DropdownMenuItem className="text-xs" onClick={handlePopOut}>
                  <ArrowSquareOutIcon className="mr-2 h-3.5 w-3.5" />
                  Open in new window
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-xs"
                  onClick={() => void handleShare()}
                >
                  <ShareNetworkIcon className="mr-2 h-3.5 w-3.5" />
                  Copy web link
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Floating: expand / collapse the rail islands (labels beside
                the icons) from the same island as the window actions. */}
            {isFloatingChrome && (
              <>
                <div
                  className={cn(
                    'mx-0.5 h-5 w-px',
                    useDarkText ? 'bg-black/10' : 'bg-white/10',
                  )}
                />
                <FloatingNavModeMenu
                  className={cn(
                    'h-8 w-8',
                    useDarkText
                      ? 'text-black/70 hover:bg-black/10 hover:text-black'
                      : 'text-white/70 hover:bg-white/10 hover:text-white',
                  )}
                />
              </>
            )}
          </TitleBarIsland>

          {/* Sidebar toggle — docked styles only; the floating toggle sits
              in the window island above. */}
          {!isFloatingChrome && (
            <>
              <div
                className={cn(
                  'mx-1 h-5 w-px',
                  useDarkText ? 'bg-black/10' : 'bg-white/10',
                )}
              />

              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleSidebar}
                className={cn(
                  'h-8 w-8',
                  useDarkText
                    ? 'text-black/70 hover:bg-black/10 hover:text-black'
                    : 'text-white/70 hover:bg-white/10 hover:text-white',
                )}
              >
                <SidebarSimpleIcon
                  className="h-[18px] w-[18px]"
                  weight="bold"
                />
                <span className="sr-only">Toggle Sidebar</span>
              </Button>

              <div
                className={cn(
                  'mx-1 h-5 w-px',
                  useDarkText ? 'bg-black/10' : 'bg-white/10',
                )}
              />
            </>
          )}

          {/* Timer + Record island */}
          <TitleBarIsland
            floating={isFloatingChrome}
            index={1}
            className="floating-titlebar-tools"
            // The island widens when a timer starts and narrows when it
            // stops; the wrappers carry `layout` too so Record glides over
            // instead of jumping.
            layout={isFloatingChrome}
          >
            {/* Timer Widget - dynamic text colors based on theme */}
            <m.div
              layout={isFloatingChrome}
              className={cn(
                useDarkText
                  ? '[&_.font-mono]:text-black [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_span]:text-black/90 [&_svg]:text-black/70'
                  : '[&_.font-mono]:text-white [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_span]:text-white/90 [&_svg]:text-white/70',
              )}
            >
              <TimeEntryWidget />
            </m.div>

            {isFloatingChrome && (
              <m.div
                layout
                className={cn(
                  'mx-0.5 h-5 w-px',
                  useDarkText ? 'bg-black/10' : 'bg-white/10',
                )}
              />
            )}

            {/* Meeting record widget - same dynamic text treatment */}
            <m.div
              layout={isFloatingChrome}
              className={cn(
                useDarkText
                  ? '[&_.font-mono]:text-black [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70'
                  : '[&_.font-mono]:text-white [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70',
              )}
            >
              <RecordButton />
            </m.div>
          </TitleBarIsland>
        </div>

        {/* Center section - OmniSearch + Benny */}
        <div
          className="flex min-w-0 flex-1 items-center justify-end px-4"
          data-tauri-drag-region
        >
          <div
            className="flex w-full max-w-xl items-center justify-end gap-2"
            data-tauri-drag-region
          >
            {/* The one Benny door on desktop: popover under the pill,
                Expand hands the thread to Conversations. Shown even while
                the companion pet is running. */}
            <TitleBarIsland floating={isFloatingChrome} index={2}>
              <AskBennyButton />
            </TitleBarIsland>
            {/* Floating: the search field is its own island; the input's
                own border and fill are cleared in theme.css. */}
            <TitleBarIsland
              floating={isFloatingChrome}
              index={3}
              className={cn(
                'floating-titlebar-search duration-base ease-ios min-w-0 gap-0 transition-[max-width]',
                isSearchExpanded
                  ? 'w-full max-w-xl px-1'
                  : // 2px each side leaves exactly the 36px (max-w-9) search
                    // button, so its right anchor (ml-auto) lands centered.
                    'w-10 max-w-10 justify-center px-0.5',
              )}
              dockedClassName={cn(
                'flex min-w-0 justify-end',
                isSearchExpanded && 'w-full',
              )}
            >
              <OmniSearch
                className="w-full"
                collapsible
                onExpandedChange={setIsSearchExpanded}
              />
            </TitleBarIsland>
          </div>
        </div>

        {/* Right section - utilities, org switcher, and user menu */}
        <div className="flex h-full flex-shrink-0 items-center gap-2 pr-4">
          <TitleBarIsland
            floating={isFloatingChrome}
            index={4}
            // theme.css sizes these controls to match the other islands.
            className="floating-titlebar-account gap-1"
          >
            {/* Wrap utility buttons with explicit text color for visibility */}
            <div
              className={cn(
                'flex items-center gap-1',
                useDarkText
                  ? 'text-black/70 [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_svg]:text-black/70'
                  : 'text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_svg]:text-white/70',
              )}
            >
              {/* Appearance lives in Settings → Account settings. */}
              <NotificationButton />
            </div>
            <TauriUpdateButton />

            <OrganizationSwitcher className="max-w-[180px]" />
            <UserMenu
              onOpenChange={onOpenChange}
              getNavigationPath={path => path}
            />
          </TitleBarIsland>
        </div>
      </div>
    </div>
  )
}

export function TauriTitleBarBasic() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setTitleBarActive } = useTauriTitleBar()
  const { useDarkText } = useTitleBarTextMode()
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const { isAuthenticated } = useAuth()
  useTauriLiquidGlass()
  const isClientPortalRoute = location.pathname.startsWith('/client-portal')
  // Portal sidebar expand/collapse lives in the titlebar on desktop, matching
  // the main app's toggle placement.
  const portalSidebarCollapsed = useClientPortalStore(
    s => s.desktopSidebarCollapsed,
  )
  const setPortalSidebarCollapsed =
    useClientPortalStore.getState().setDesktopSidebarCollapsed
  const portalEntitySegment = isClientPortalRoute
    ? location.pathname.split('/').filter(Boolean)[1]
    : undefined
  const portalEntityId =
    portalEntitySegment && portalEntitySegment !== 'invite'
      ? portalEntitySegment
      : undefined
  // The pill hides itself when portal Benny is off; skip its island too, or
  // an empty pill sits in the title bar.
  const isPortalBennyAvailable = usePortalAskBennyAvailable(
    isAuthenticated ? portalEntityId : undefined,
  )

  useEffect(() => {
    setTitleBarActive(true)
    return () => setTitleBarActive(false)
  }, [setTitleBarActive])

  const handleBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    reloadPageWithRecordingGuard()
  }

  // Client portal routes join the floating navigation style
  // (config/rail-areas.ts) like the main title bar: the bar takes the
  // portal's ground and its controls gather into islands. Colors follow the
  // portal branding (theme.css, "Client portal: floating navigation style").
  // Native glass is left off so the bar matches the portal layout's ground.
  const isPortalFloating =
    isClientPortalRoute && NAVIGATION_STYLE === 'floating'

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && !isPortalFloating && 'tauri-glass-titlebar',
        isPortalFloating && 'floating-titlebar',
      )}
      data-tauri-drag-region
      data-nav-style={isPortalFloating ? 'floating' : undefined}
      data-glass={isPortalFloating ? 'false' : undefined}
      data-portal-titlebar={isPortalFloating ? 'true' : undefined}
    >
      <div
        className={cn('flex h-[52px] w-full items-center')}
        data-tauri-drag-region
      >
        <div
          className={cn(
            'flex h-full flex-shrink-0 items-center pl-[100px] pr-3',
            isPortalFloating ? 'gap-2' : 'gap-1',
          )}
          data-tauri-drag-region
        >
          <TitleBarIsland floating={isPortalFloating} index={0}>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className={cn(
                'h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Go back"
            >
              <ArrowLeftIcon className="h-[16px] w-[16px]" weight="bold" />
              <span className="sr-only">Go back</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className={cn(
                'h-8 w-8',
                useDarkText
                  ? 'text-black/50 hover:bg-black/10 hover:text-black'
                  : 'text-white/50 hover:bg-white/10 hover:text-white',
              )}
              title="Refresh"
            >
              <ArrowClockwiseIcon className="h-[16px] w-[16px]" weight="bold" />
              <span className="sr-only">Refresh</span>
            </Button>
            {isClientPortalRoute && (
              <>
                <div
                  className={cn(
                    'h-5 w-px',
                    isPortalFloating ? 'mx-0.5' : 'mx-1',
                    useDarkText ? 'bg-black/10' : 'bg-white/10',
                  )}
                />
                {isPortalFloating ? (
                  <ClientPortalNavModeMenu />
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setPortalSidebarCollapsed(!portalSidebarCollapsed)
                    }
                    className={cn(
                      'h-8 w-8',
                      useDarkText
                        ? 'text-black/70 hover:bg-black/10 hover:text-black'
                        : 'text-white/70 hover:bg-white/10 hover:text-white',
                    )}
                    title="Toggle sidebar"
                  >
                    <SidebarSimpleIcon
                      className="h-[18px] w-[18px]"
                      weight="bold"
                    />
                    <span className="sr-only">Toggle sidebar</span>
                  </Button>
                )}
              </>
            )}
          </TitleBarIsland>
        </div>

        <div
          className="flex min-w-0 flex-1 items-center justify-center px-4"
          data-tauri-drag-region
        />

        {/* Right section - mode toggle and user menu (only when authenticated) */}
        <div className="flex h-full flex-shrink-0 items-center gap-2 pr-4">
          {/* Portal Benny: the client's own conversations, never org Benny. */}
          {isClientPortalRoute &&
          isAuthenticated &&
          portalEntityId &&
          isPortalBennyAvailable ? (
            <TitleBarIsland floating={isPortalFloating} index={1}>
              <PortalAskBennyButton entityId={portalEntityId} />
            </TitleBarIsland>
          ) : null}
          <TitleBarIsland
            floating={isPortalFloating}
            index={1}
            className="gap-1"
          >
            <div
              className={cn(
                'flex items-center gap-1',
                useDarkText
                  ? 'text-black/70 [&_button:hover]:bg-black/10 [&_button:hover]:text-black [&_button]:text-black/70 [&_svg]:text-black/70'
                  : 'text-white/70 [&_button:hover]:bg-white/10 [&_button:hover]:text-white [&_button]:text-white/70 [&_svg]:text-white/70',
              )}
            >
              <ModeToggle
                showSidebarTheme={!isClientPortalRoute}
                showGlassToggle={!isClientPortalRoute}
              />
              {isClientPortalRoute && isAuthenticated ? (
                <ClientPortalNotificationCenter entityId={portalEntityId} />
              ) : null}
            </div>
            <TauriUpdateButton />
            {isClientPortalRoute && isAuthenticated ? (
              <div
                className={cn(
                  'max-w-[220px]',
                  // Floating: the islands take the portal's colors, and the
                  // title-bar CSS drives text from them; the sidebar-theme
                  // text classes would fight that on branded islands.
                  isPortalFloating
                    ? undefined
                    : useDarkText
                      ? '[&_.text-muted-foreground]:text-black/55 [&_button:hover]:bg-black/10 [&_button]:text-black/80'
                      : '[&_.text-muted-foreground]:text-white/60 [&_button:hover]:bg-white/10 [&_button]:text-white/90',
                )}
              >
                <ClientPortalAccessSwitcher />
              </div>
            ) : null}
            {isAuthenticated && (
              <UserMenu
                hideOrgSettings
                showHelpItems={false}
                getNavigationPath={path => path}
              />
            )}
          </TitleBarIsland>
        </div>
      </div>
    </div>
  )
}

/**
 * Height of the TauriTitleBar in pixels - used for offsetting content
 * 52px = h-[52px] for proper traffic light centering
 */
export const TAURI_TITLE_BAR_HEIGHT = 52

/**
 * Minimal navigation bar for popup/focus windows
 * Back and reload buttons only - thin and unobtrusive
 * No pop-out button since we're already in a popup window
 */
export function PopupNavBar() {
  const navigate = useNavigate()
  const location = useLocation()
  const tauriGlassEnabled = useUIPreferencesStore(s => s.tauriGlassEnabled)
  const { useDarkText } = useTitleBarTextMode()
  useTauriLiquidGlass()
  const isSettingsWindow = location.pathname === '/desktop-settings'

  const handleBack = () => navigate(-1)
  const handleRefresh = () => reloadPageWithRecordingGuard()

  return (
    <div
      className={cn(
        'themed-titlebar bg-sidebar fixed left-0 right-0 top-0 z-50 transition-colors duration-300',
        tauriGlassEnabled && 'tauri-glass-titlebar',
      )}
      data-tauri-drag-region
    >
      <div
        className="relative flex h-8 items-center pl-[76px]"
        data-tauri-drag-region
      >
        {isSettingsWindow && (
          <span
            className={cn(
              'pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-semibold',
              useDarkText ? 'text-black/65' : 'text-white/70',
            )}
            data-tauri-drag-region
          >
            Settings
          </span>
        )}
        {!isSettingsWindow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className={cn(
              'h-6 w-6',
              useDarkText
                ? 'text-black/60 hover:bg-black/10 hover:text-black'
                : 'text-white/60 hover:bg-white/10 hover:text-white',
            )}
            title="Go back"
          >
            <ArrowLeftIcon className="h-3.5 w-3.5" weight="bold" />
          </Button>
        )}
        {!isSettingsWindow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className={cn(
              'h-6 w-6',
              useDarkText
                ? 'text-black/60 hover:bg-black/10 hover:text-black'
                : 'text-white/60 hover:bg-white/10 hover:text-white',
            )}
            title="Reload"
          >
            <ArrowClockwiseIcon className="h-3.5 w-3.5" weight="bold" />
          </Button>
        )}
      </div>
    </div>
  )
}

export const POPUP_NAV_BAR_HEIGHT = 32
